import { Model } from '@technology/web3d';
import { Material, Object3D } from 'three';

interface MaterialWithOpacity extends Material {
  uniforms?: {
    opacity?: { value: number };
  };
  updateTransparency?(): void;
}

interface GeometryWithMaterial extends Object3D {
  material?: Material | Material[];
}

interface Tiles3DModelLike extends Model {
  tilesRenderer?: {
    onLoadModel?(scene: Object3D, tile: unknown): void;
  };
  updateTiles?(cameraMove?: boolean): void;
}

const tilesetOpacities = new WeakMap<Model, number>();
const hookedTilesetRenderers = new WeakSet<object>();
const pendingOpacityUpdates = new Map<Model, number>();
let opacityAnimationFrameId: number | undefined;

function forEachMaterial(
  material: Material | Material[] | undefined,
  callback: (mat: MaterialWithOpacity) => void
): void {
  if (!material) {
    return;
  }
  if (Array.isArray(material)) {
    for (const mat of material) {
      callback(mat as MaterialWithOpacity);
    }
    return;
  }
  callback(material as MaterialWithOpacity);
}

function applyOpacityToMaterial(
  material: MaterialWithOpacity,
  opacity: number
): void {
  // Web3D mesh materials (e.g. tileset tiles) share renderingManager.uniforms via
  // _globalUniforms. Only set per-material opacity — never assign to
  // _globalUniforms.globalOpacity, which must remain a Uniform wrapper.
  //
  // Only enable the transparent render path when reducing opacity. Never set
  // transparent=false at full slider opacity — tileset materials can have
  // inherent partial transparency (texture alpha, depth peeling) that must
  // keep the transparency pipeline active.
  if (opacity < 1) {
    material.transparent = true;
  }
  material.opacity = opacity;
  if (typeof material.updateTransparency === 'function') {
    material.updateTransparency();
  } else if (material.uniforms?.opacity) {
    material.uniforms.opacity.value = opacity;
  }
}

function applyOpacityToObject3D(root: Object3D, opacity: number): void {
  root.traverse((child) => {
    const object = child as GeometryWithMaterial;
    if (!object.material) {
      return;
    }
    forEachMaterial(object.material, (mat) =>
      applyOpacityToMaterial(mat, opacity)
    );
  });
}

function hookTilesRendererOnLoad(model: Tiles3DModelLike): void {
  const tilesRenderer = model.tilesRenderer;
  if (!tilesRenderer || hookedTilesetRenderers.has(tilesRenderer)) {
    return;
  }
  hookedTilesetRenderers.add(tilesRenderer);
  const previousOnLoadModel = tilesRenderer.onLoadModel;
  tilesRenderer.onLoadModel = (scene, tile) => {
    previousOnLoadModel?.(scene, tile);
    const opacity = tilesetOpacities.get(model);
    if (opacity !== undefined) {
      applyOpacityToObject3D(scene, opacity);
    }
  };
}

function applyTilesetModelOpacityNow(model: Model, opacity: number): void {
  applyOpacityToObject3D(model.root, opacity);
  (model as Tiles3DModelLike).updateTiles?.();
}

function flushPendingOpacityUpdates(): void {
  opacityAnimationFrameId = undefined;
  for (const [model, opacity] of pendingOpacityUpdates) {
    applyTilesetModelOpacityNow(model, opacity);
  }
  pendingOpacityUpdates.clear();
}

function scheduleTilesetOpacityUpdate(model: Model, opacity: number): void {
  tilesetOpacities.set(model, opacity);
  hookTilesRendererOnLoad(model as Tiles3DModelLike);
  pendingOpacityUpdates.set(model, opacity);
  if (opacityAnimationFrameId !== undefined) {
    return;
  }
  opacityAnimationFrameId = requestAnimationFrame(flushPendingOpacityUpdates);
}

export function setTilesetModelOpacity(
  model: Model | null | undefined,
  opacity: number
): void {
  if (!model?.root) {
    return;
  }
  scheduleTilesetOpacityUpdate(model, opacity);
}
